<template>
  <el-card shadow="hover">
    <div slot="header" class="clearfix">
      <span>
        Target Location
        <small>
          <el-tooltip placement="right" effect="light">
            <span slot="content"
              >Set the target information to define where you want the data
              sent. For Planning<br />Analytics targets, each record processed
              will be sent to all base level descendants<br />of the
              consolidation or to a single base level element of your
              choosing.</span
            >
            <i
              class="el-icon-info"
              style="position: relative; left: 2px; bottom: 4px"
            />
          </el-tooltip>
        </small>
      </span>
    </div>
    <el-form label-position="top">
      <el-form-item v-if="sourceType === 'Single Cube'">
        <span slot="label">
          Target Type
          <small v-if="targetType === 'File'">
            <el-tooltip placement="right" effect="light">
              <span slot="content"
                >The file name will be generated automatically by DataMaestro,
                and the<br />file will be sent to the Planning Analytics
                server's default export location.</span
              >
              <i
                class="el-icon-info"
                style="position: relative; left: 2px; bottom: 4px"
              />
            </el-tooltip>
          </small>
        </span>
        <el-select
          filterable
          style="width: 100%"
          v-model="targetType"
          @change="targetTypeSelected"
          :disabled="envRights === 'Run'"
        >
          <el-option
            value="Same Cube"
            label="Same Cube"
            v-if="sourceType === 'Single Cube'"
          />
          <el-option
            value="Different Cube"
            label="Different Cube"
            v-if="sourceType === 'Single Cube'"
          />
          <el-option value="Cube" label="Cube" v-if="sourceType === 'Value'" />
          <el-option
            value="Multiple Cubes"
            label="Multiple Cubes"
            v-if="sourceType === 'Multiple Cubes'"
          />
          <el-option value="File" label="File" />
        </el-select>
      </el-form-item>
      <el-form-item
        label="Insertion Method"
        v-if="type !== 'Zero Out' && targetType !== 'File'"
      >
        <el-select
          filterable
          style="width: 100%"
          v-model="method"
          @change="methodSelected"
          :disabled="envRights === 'Run'"
        >
          <el-option value="Replace" label="Replace Target Value" />
          <el-option value="Add" label="Add to Target Value" />
        </el-select>
      </el-form-item>
      <el-form-item
        label="Cube"
        v-if="
          (sourceType !== 'Multiple Cubes' && targetType !== 'File') ||
          (type === 'Zero Out' && targetCube)
        "
      >
        <el-select
          filterable
          style="width: 100%"
          v-model="targetCube"
          @change="getCubeDims"
          :disabled="targetType === 'Same Cube' || envRights === 'Run'"
          placeholder
          :class="{ error: checkValid && $v.targetCube.$invalid }"
        >
          <el-option
            v-for="item in cubeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
            :disabled="item.disabled"
          />
        </el-select>
      </el-form-item>
    </el-form>
    <el-form
      label-position="top"
      v-if="
        ((sourceType !== 'Multiple Cubes' && targetType !== 'File') ||
          type === 'Zero Out') &&
        targetCube
      "
    >
      <el-form-item
        v-for="({ name }, index) in targetCubeDims"
        :key="index"
        :label="name"
      >
        <b-input-group :prepend="targetCubeDims[index].selectionType" size="sm">
          <b-form-input
            :disabled="
              (targetType === 'Same Cube' && !cubeDimPlaceholders[index]) ||
              envRights === 'Run'
            "
            v-model="$v.targetCubeDims.$model[index].display"
            :class="{
              error: checkValid && $v.targetCubeDims.$each[index].$invalid,
            }"
            :placeholder="cubeDimPlaceholders[index]"
            type="text"
            size="sm"
            readonly
            :style="{
              backgroundColor:
                (targetType !== 'Same Cube' ||
                  (targetType === 'Same Cube' && cubeDimPlaceholders[index])) &&
                envRights !== 'Run' &&
                '#fff',
              cursor:
                (targetType !== 'Same Cube' ||
                  (targetType === 'Same Cube' && cubeDimPlaceholders[index])) &&
                envRights !== 'Run' &&
                'pointer',
            }"
            @click="dimSelected(targetCubeDims[index], index, 'targetCube')"
          />
          <b-input-group-append
            v-if="
              targetType !== 'Same Cube' ||
              (targetType === 'Same Cube' && cubeDimPlaceholders[index])
            "
          >
            <b-button
              variant="outline-dark"
              v-if="
                [
                  'Attribute',
                  'Consolidated',
                  'Multi-Select',
                  'Subset',
                ].includes(targetCubeDims[index].selectionType) &&
                [
                  'Attribute',
                  'Consolidated',
                  'Multi-Select',
                  'Subset',
                ].includes(sourceCubeDims[index]?.selectionType)
              "
              @click="showPreview(index)"
            >
              Mapping
            </b-button>
          </b-input-group-append>
          <b-input-group-append
            v-if="
              targetType !== 'Same Cube' ||
              (targetType === 'Same Cube' && cubeDimPlaceholders[index])
            "
          >
            <b-button
              :disabled="envRights === 'Run'"
              size="sm"
              @click="clearSelection('targetCube', index)"
            >
              <b-icon icon="x" />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </el-form-item>
    </el-form>
    <el-collapse
      v-model="collapseItems"
      style="margin-top: 10px"
      v-if="sourceType === 'Multiple Cubes'"
    >
      <el-collapse-item
        name="1"
        title="Dimensions"
        :disabled="targetDims.length === 0"
      >
        <el-form label-position="top">
          <div
            :class="index === 1 ? 'border-first' : 'border'"
            v-for="index in targetDims.length"
            :key="index"
          >
            <el-form-item label="Dimension" style="margin: 0 10px 10px 10px">
              <b-form-select
                v-model="targetDims[index - 1].name"
                size="sm"
                :options="dims"
                disabled
              />
            </el-form-item>
            <el-form-item label="Element" style="margin: 0 10px 10px 10px">
              <b-input-group
                :prepend="targetDims[index - 1].selectionType"
                size="sm"
              >
                <b-form-input
                  :disabled="!dimPlaceholders[index - 1] || envRights === 'Run'"
                  v-model="$v.targetDims.$model[index - 1].display"
                  :class="{
                    error:
                      checkValid && $v.targetDims.$each[index - 1].$invalid,
                  }"
                  :placeholder="dimPlaceholders[index - 1]"
                  type="text"
                  size="sm"
                  readonly
                  :style="{
                    backgroundColor:
                      dimPlaceholders[index - 1] &&
                      envRights !== 'Run' &&
                      '#fff',
                    cursor:
                      dimPlaceholders[index - 1] &&
                      envRights !== 'Run' &&
                      'pointer',
                  }"
                  @click="
                    dimSelected(targetDims[index - 1], index - 1, 'target')
                  "
                />
                <b-input-group-append v-if="dimPlaceholders[index - 1]">
                  <b-button
                    :disabled="envRights === 'Run'"
                    size="sm"
                    @click="clearSelection('target', index - 1)"
                  >
                    <b-icon icon="x" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </el-form-item>
          </div>
        </el-form>
      </el-collapse-item>
    </el-collapse>
  </el-card>
</template>

<script>
import { required, requiredIf } from "vuelidate/lib/validators";
import { mapState } from "vuex";

export default {
  props: {
    taskType: String,
    taskSourceType: String,
    taskSourceCube: String,
    taskSourceCubeDims: Array,
    taskSourceDims: Array,
    taskTargetType: String,
    taskMethod: String,
    taskTargetCube: String,
    taskTargetCubeDims: Array,
    taskTargetDims: Array,
    taskCubes: Array,
    taskDims: Array,
    taskCheckValid: Boolean,
  },
  data() {
    return {
      type: this.taskType,
      sourceType: this.taskSourceType,
      sourceCube: this.taskSourceCube,
      sourceCubeDims: this.taskSourceCubeDims,
      sourceDims: this.taskSourceDims,
      targetType: this.taskTargetType,
      method: this.taskMethod,
      targetCube: this.taskTargetCube,
      targetCubeDims: this.taskTargetCubeDims,
      targetDims: this.taskTargetDims,
      cubes: this.taskCubes,
      dims: this.taskDims,
      checkValid: false,
      // cubeDimPlaceholders: this.taskSourceCubeDims
      //   ? this.taskSourceCubeDims.map((dim) => {
      //       return this.type === "Zero Out" ? undefined : dim.display;
      //     })
      //   : [],
      // dimPlaceholders: this.taskSourceDims
      //   ? this.taskSourceDims.map((dim) => {
      //       return this.type === "Zero Out" ? undefined : dim.display;
      //     })
      //   : [],
      collapseItems: [],
    };
  },
  computed: {
    ...mapState(["envRights"]),
    cubeOptions() {
      const options = this.taskCubes.map((name) => {
        return { value: name, label: name };
      });
      return options;
    },
    dimOptions() {
      const options = this.taskDims.map((name) => {
        return { value: name, label: name };
      });
      return options;
    },
    dimPlaceholders() {
      return this.taskSourceDims
        ? this.taskSourceDims.map((dim) => {
            return this.type === "Zero Out" ? undefined : dim.display;
          })
        : [];
    },
    cubeDimPlaceholders() {
      return this.taskSourceCubeDims
        ? this.taskSourceCubeDims.map((dim) => {
            return this.type === "Zero Out" ? undefined : dim.display;
          })
        : [];
    },
  },
  watch: {
    taskType(value) {
      this.type = value;
      if (value === "Zero Out" && this.targetType === "File") {
        this.targetType = "Cube";
        this.targetTypeSelected("Cube");
      }
    },
    taskSourceType(value) {
      this.sourceType = value;
      if (this.sourceType === "Multiple Cubes") {
        this.targetType = "Multiple Cubes";
        this.dimPlaceholders = this.sourceDims.map((dim) => {
          return dim.selectionType === "Multi-Select"
            ? dim.selection.map((el) => el.name).join(", ")
            : dim.selection;
        });
      } else if (this.sourceType === "Single Cube") {
        this.targetType = "Same Cube";
        this.targetCube = this.sourceCube;
      }
      if (this.targetCube) this.getCubeDims();
      this.setCubeDimPlaceholders();
    },
    taskSourceCube(value) {
      if (value) {
        this.sourceCube = value;
        if (this.targetType === "Same Cube") {
          this.targetCube = this.sourceCube;
        }
        this.getCubeDims();
      }
    },
    taskSourceCubeDims(value) {
      this.sourceCubeDims = value;
      this.setCubeDimPlaceholders();
    },
    taskSourceDims(value) {
      this.sourceDims = value;
      this.dimPlaceholders = value.map((dim, index) => {
        if (this.targetDims[index] && !dim.selection) {
          this.targetDims[index].selection = null;
          this.targetDims[index].selectionAlias = null;
          this.targetDims[index].selectionType = null;
        }
        return dim.display;
      });
      if (value.length === 0) {
        this.collapseItems = [];
      } else if (!this.targetDims.find((dim) => dim.selection)) {
        this.collapseItems = [1, "1"];
      }
    },
    taskTargetType(value) {
      this.targetType = value;
    },
    taskMethod(value) {
      this.method = value === null ? "Add" : value;
    },
    taskTargetCube(value) {
      this.targetCube = value;
      this.setCubeDimPlaceholders();
    },
    taskTargetCubeDims(value) {
      this.targetCubeDims = value;
      this.setCubeDimPlaceholders();
    },
    taskTargetDims(value) {
      this.targetDims = value;
    },
    taskCubes(value) {
      this.cubes = value;
    },
    taskDims(value) {
      this.dims = value;
    },
    taskCheckValid(value) {
      this.checkValid = value;
    },
  },
  methods: {
    async showPreview(index) {
      this.$emit("showMappingPreview", { index });
    },
    formatForDisplay(value) {
      if (value && value[0] === "[" && value[value.length - 1] === "]") {
        value = JSON.parse(value);
        return `${value.length} Elements`;
      }
      return value;
    },
    /**
     * Updates the interface after selecting a task insertion method
     * @function
     * @param {String} method - Insertion method of the task target
     */
    methodSelected(method) {
      this.$emit("methodSelected", method);
    },
    /**
     * Updates the cube dimension placeholders based on the type and cube values
     * @function
     */
    setCubeDimPlaceholders() {
      if (this.type !== "Zero Out")
        this.cubeDimPlaceholders =
          this.sourceType === "Value"
            ? []
            : this.sourceCube === this.targetCube
            ? this.sourceCubeDims.map((dim, index) => {
                if (this.targetCubeDims[index] && !dim.selection) {
                  this.targetCubeDims[index].selection = null;
                  this.targetCubeDims[index].selectionAlias = null;
                  this.targetCubeDims[index].selectionType = null;
                }
                return dim.display;
              })
            : this.targetCubeDims.map((targetDim) => {
                const index = this.sourceCubeDims.findIndex(
                  (sourceDim) => sourceDim.name === targetDim.name
                );
                return index === -1 ? "" : this.sourceCubeDims[index].display;
              });
    },
    /**
     * Clears the dimension element selection
     * @function
     * @param {String} type - Type of the dimension
     * @param {Number} index - Index of the dimension
     */
    clearSelection(type, index) {
      this.$emit("selectionSubmitted", {
        type,
        index,
        selection: null,
        hierarchy: null,
        selectionType: null,
        selectionAlias: null,
      });
    },
    /**
     * Updates the dimension selection and triggers the modal
     * @function
     * @param {String} dim - Name of the dimension
     * @param {Number} index - Index of the dimension
     * @param {String} type - Type of the dimension
     */
    dimSelected(dim, index, type) {
      this.$emit("dimSelected", {
        dim,
        index,
        type,
      });
    },
    /**
     * Fetches the list of dimensions associated with a particular cube
     * @function
     */
    getCubeDims() {
      this.$emit("getCubeDims", {
        card: "target",
        cube: this.targetCube,
      });
    },
    /**
     * Updates the interface after selecting a task target type
     * @function
     * @param {String} type - Type of the task target
     */
    targetTypeSelected(type) {
      if (type === "Same Cube" && this.sourceCube) {
        this.targetCube = this.sourceCube;
        this.$emit("getCubeDims", {
          card: "target",
          cube: this.targetCube,
        });
      }
      this.$emit("targetTypeSelected", type);
    },
  },
  validations() {
    return {
      targetType: { required },
      targetCube: {
        required: requiredIf(() => {
          return (
            this.targetType !== "Multiple Cubes" && this.targetType !== "File"
          );
        }),
      },
      targetCubeDims: {
        $each: {
          selection: {
            required: requiredIf((value) => {
              if (this.targetType === "Different Cube") {
                const sourceDim = this.sourceCubeDims.find(
                  (dim) => value.name === dim.name
                );
                return sourceDim ? false : !value.selection;
              } else {
                return false;
              }
            }),
          },
        },
      },
      //TODO: Should this be required?
      targetDims: {
        $each: {
          selection: {
            required: requiredIf(() => {
              return (
                this.sourceType === "Multiple Cubes" &&
                !this.targetDims.find((dim) => dim.selection)
              );
            }),
          },
        },
      },
    };
  },
};
</script>

<style scoped>
.error :deep(.el-input__inner) {
  border: 1px solid red;
  border-radius: 4px;
}
/* Delete when BootstrapVue objects removed */
.form-control.form-control-sm.error {
  border: 1px solid red;
  border-radius: 4px;
}
.button {
  background-color: #b3282d;
  color: white;
  border-color: white;
  padding: 10px 15px;
}
.button:focus {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.button:hover {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.border {
  margin-top: 20px;
  padding-bottom: 8px;
  border: 1px #dcdfe6 solid !important;
  border-radius: 4px;
}
.border-first {
  margin-top: 5px;
  padding-bottom: 8px;
  border: 1px #dcdfe6 solid !important;
  border-radius: 4px;
}
</style>
